/**
 * DIN EN 1991-1-4 / NA Tafel 3.25 — simplified gust velocity pressures q_p [N/m²]
 * for structures up to 25 m height.
 *
 * Table values are given in kN/m²; stored values are multiplied by 1000.
 */
export type SimplifiedMethodStandort =
  | 'Binnenland'
  | 'KuesteOstseeInseln'
  | 'KuesteNordOstseeInselnOstsee'
  | 'InselnNordsee'

export type SimplifiedQpHeightBucket = 'h_le_10' | 'h_10_18' | 'h_18_25'

type SimplifiedQpRow = Record<SimplifiedQpHeightBucket, number | null>

const SIMPLIFIED_QP_TABLE_KNM2_TO_NM2: Record<
  WindZone,
  Partial<Record<SimplifiedMethodStandort, SimplifiedQpRow>>
> = {
  1: {
    Binnenland: { h_le_10: 500, h_10_18: 650, h_18_25: 750 },
  },
  2: {
    Binnenland: { h_le_10: 650, h_10_18: 800, h_18_25: 900 },
    KuesteOstseeInseln: { h_le_10: 850, h_10_18: 1000, h_18_25: 1100 },
  },
  3: {
    Binnenland: { h_le_10: 800, h_10_18: 950, h_18_25: 1100 },
    KuesteOstseeInseln: { h_le_10: 1050, h_10_18: 1200, h_18_25: 1300 },
  },
  4: {
    Binnenland: { h_le_10: 950, h_10_18: 1150, h_18_25: 1300 },
    KuesteNordOstseeInselnOstsee: { h_le_10: 1250, h_10_18: 1400, h_18_25: 1550 },
    InselnNordsee: { h_le_10: 1400, h_10_18: null, h_18_25: null },
  },
}

export const simplifiedMethodStandortLabels: Record<SimplifiedMethodStandort, string> = {
  Binnenland: 'Binnenland',
  KuesteOstseeInseln: 'Küste und Inseln der Ostsee',
  KuesteNordOstseeInselnOstsee: 'Küste der Nord- und Ostsee und Inseln der Ostsee',
  InselnNordsee: 'Inseln der Nordsee',
}

const STANDORT_OPTIONS_BY_WIND_ZONE: Record<WindZone, SimplifiedMethodStandort[]> = {
  1: ['Binnenland'],
  2: ['Binnenland', 'KuesteOstseeInseln'],
  3: ['Binnenland', 'KuesteOstseeInseln'],
  4: ['Binnenland', 'KuesteNordOstseeInselnOstsee', 'InselnNordsee'],
}

export const getSimplifiedMethodStandortOptions = (
  windZone: WindZone,
): { value: SimplifiedMethodStandort; label: string }[] =>
  STANDORT_OPTIONS_BY_WIND_ZONE[windZone].map(value => ({
    value,
    label: simplifiedMethodStandortLabels[value],
  }))

export const isSimplifiedMethodStandortValidForWindZone = (
  windZone: WindZone,
  standort: SimplifiedMethodStandort,
): boolean => STANDORT_OPTIONS_BY_WIND_ZONE[windZone].includes(standort)

export const normalizeSimplifiedMethodStandort = (
  windZone: WindZone,
  standort: SimplifiedMethodStandort | undefined,
): SimplifiedMethodStandort => {
  if (standort && isSimplifiedMethodStandortValidForWindZone(windZone, standort)) {
    return standort
  }
  return 'Binnenland'
}

export const getSimplifiedQpHeightBucket = (buildingHeightM: number): SimplifiedQpHeightBucket => {
  if (buildingHeightM <= 10) {
    return 'h_le_10'
  }
  if (buildingHeightM <= 18) {
    return 'h_10_18'
  }
  return 'h_18_25'
}

export const lookupSimplifiedGustPressureQp = (
  windZone: WindZone,
  buildingHeightForQpLookup: number,
  standort: SimplifiedMethodStandort = 'Binnenland',
): number | null => {
  const zoneTable = SIMPLIFIED_QP_TABLE_KNM2_TO_NM2[windZone]
  const standortRow = zoneTable?.[standort]
  if (!standortRow) {
    return null
  }

  const heightBucket = getSimplifiedQpHeightBucket(buildingHeightForQpLookup)
  return standortRow[heightBucket] ?? null
}
